/**
 * ApiTokenSettings — /w/:slug/app/settings/api
 *
 * PR-Cluster-7-d (docs/ux-design-plan.md §10) — Marcus persona's
 * pull-integration story counterpart to WebhookSettings. Backend at
 * packages/cloudflare/src/routes/api-tokens.ts (owner/admin gated),
 * shipped in Phase C per §4.3 of uc-2-provenance-api-spec.md.
 *
 * Scope of this first slice:
 *   - List active + revoked tokens (SHA-256 stored + 8-char prefix
 *     display; last_used_at + last_used_ip_hash surfaced)
 *   - Create token → returns plaintext ONCE with copy affordance
 *   - Revoke with typed reason
 *
 * PR-Cluster-7-c polish:
 *   - Copyable curl example against /api/w/:slug/readiness/:profileId
 *     using a placeholder token (§4.4 REST card).
 *
 * V108-1-1 (ADR-0087 §2, research B-3): create picks what the token is FOR
 * (a purpose from GET …/settings/api/purposes), never raw scopes, plus the
 * system when the purpose is system-bound. "Read this workspace's data"
 * stays as the plain read token.
 *
 * V108-8-7: a system-optional purpose ("Command-line setup") defaults to "A new system (register
 * from the terminal)", which mints `system:register` only; picking a system mints the bound set.
 *
 * Deferred:
 *   - Usage graph / request-count-in-last-24h (needs a usage-
 *     analytics endpoint that hasn't shipped yet)
 */

import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  createApiToken,
  listApiTokenPurposes,
  listApiTokens,
  listWorkspaceSystems,
  purposeSystemMode,
  revokeApiToken,
  type ApiTokenSummary,
  type CreatedApiTokenResponse,
} from '../lib/api';
import { fmtDate } from '../lib/format';

export function ApiTokenSettings() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const canManage =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';

  const [error, setError] = useState<string | null>(null);
  // V108-1-1: ?purpose=…&system=… (the link CI setup sends) opens the form preselected.
  const [params] = useSearchParams();
  const [showCreate, setShowCreate] = useState(params.has('purpose'));
  const [createdRevealed, setCreatedRevealed] = useState<CreatedApiTokenResponse | null>(null);

  const tokens = useWorkspaceQuery(
    (slug) => queryKeys.apiTokens(slug),
    (slug) => listApiTokens(slug),
  );

  if (!slug) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '68rem' }}>
      <PageHeader
        title="API tokens"
        subtitle="Pull readiness + register data into your GRC or observability stack."
        right={
          canManage ? (
            <Button variant="primary" compact onClick={() => setShowCreate((s) => !s)}>
              {showCreate ? 'Cancel' : '+ Create token'}
            </Button>
          ) : null
        }
      />

      {error ? <Alert variant="error">{error}</Alert> : null}
      {!canManage ? (
        <Alert variant="info">
          Only workspace owners and admins can create or revoke API tokens. You can view existing tokens below.
        </Alert>
      ) : null}

      {createdRevealed ? (
        <TokenRevealBanner
          created={createdRevealed}
          onDismiss={() => setCreatedRevealed(null)}
        />
      ) : null}

      {showCreate && canManage ? (
        <CreateTokenForm
          slug={slug}
          onCreated={(created) => {
            setCreatedRevealed(created);
            setShowCreate(false);
            tokens.refetch();
          }}
          onError={setError}
          onCancel={() => setShowCreate(false)}
        />
      ) : null}

      {tokens.loading ? (
        <Spinner label="Loading tokens" />
      ) : tokens.error ? (
        <Alert variant="error">{tokens.error}</Alert>
      ) : !tokens.data || tokens.data.tokens.length === 0 ? (
        <EmptyState
          title="No API tokens"
          description="Create a token to authenticate machine consumers against the gacpro REST API."
          action={
            canManage ? (
              <Button variant="primary" onClick={() => setShowCreate(true)}>
                + Create token
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {tokens.data.tokens.map((t) => (
            <TokenCard
              key={t.token_id}
              slug={slug}
              token={t}
              canManage={canManage}
              onError={setError}
              onChanged={tokens.refetch}
            />
          ))}
        </div>
      )}

      {/* PR-Cluster-7-c polish — copy-paste onboarding for consumers
          who just created a token and are wondering "now what". */}
      <CurlExample slug={slug} />
    </div>
  );
}

// ---- Copy-paste curl example ----------------------------------------

function CurlExample({ slug }: { slug: string }) {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://gacpro.io';
  const snippet = `# Set your token (from the one-time reveal above).
export FEDSEC_TOKEN=fdst_...

# Fetch a readiness snapshot for a specific compliance profile.
# Replace PROFILE_ID with the id from GET /api/w/${slug}/profiles.
curl -sS \\
  -H "Authorization: Bearer $FEDSEC_TOKEN" \\
  -H "Accept: application/json" \\
  "${base}/api/w/${slug}/readiness/PROFILE_ID" | jq '.readiness.rollup'

# Every response is methodology-pinned — the .methodology object tells
# you which formula version produced the numbers, so downstream systems
# can detect a restated snapshot vs a live-drift shift.`;
  return (
    <details
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          padding: 'var(--space-3) var(--space-4)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text)',
          fontWeight: 500,
          listStyle: 'none',
        }}
      >
        <span aria-hidden style={{ marginRight: 'var(--space-2)', color: 'var(--text-dim)' }}>
          ▸
        </span>
        Try the API — copy-paste curl example
      </summary>
      <div style={{ padding: '0 var(--space-4) var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
        <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          Full REST reference lives at{' '}
          <a
            href="/api/openapi.json"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}
          >
            /api/openapi.json
          </a>
          .
        </p>
        <CodeSnippet body={snippet} />
      </div>
    </details>
  );
}

function CodeSnippet({ body }: { body: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignored
    }
  };
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px var(--space-2)',
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderBottom: 0,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        <span>curl</span>
        <button
          type="button"
          onClick={copy}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 0,
            color: 'var(--accent)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-3xs)',
            padding: 0,
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </figcaption>
      <pre
        style={{
          margin: 0,
          padding: 'var(--space-3)',
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text)',
          overflow: 'auto',
          maxHeight: '22rem',
        }}
      >
        {body}
      </pre>
    </figure>
  );
}

// ---- One-time reveal banner ------------------------------------------

function TokenRevealBanner(props: {
  created: CreatedApiTokenResponse;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.created.plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (private window, etc.) — triple-click still works
    }
  };
  return (
    <section
      aria-label="New API token"
      style={{
        border: '1px solid var(--band-amber)',
        borderLeftWidth: '4px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--warning-bg)',
        padding: 'var(--space-4) var(--space-5)',
        color: 'var(--warning-fg)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <strong style={{ fontSize: 'var(--font-size-md)', fontWeight: 700 }}>
          Token created — copy it now
        </strong>
        <button
          type="button"
          onClick={props.onDismiss}
          aria-label="Dismiss"
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 0,
            color: 'var(--warning-fg)',
            fontSize: 'var(--font-size-md)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)' }}>
        The plaintext token below will never be shown again. Store it in your consumer's secret
        manager, then rotate here if it leaks. gacpro stores only a SHA-256 hash and an
        8-character prefix.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'stretch' }}>
        <code
          style={{
            flex: 1,
            display: 'block',
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text)',
            overflow: 'auto',
            wordBreak: 'break-all',
          }}
        >
          {props.created.plaintext}
        </code>
        <Button variant="secondary" compact onClick={copy}>
          {copied ? '✓ Copied' : 'Copy'}
        </Button>
      </div>
    </section>
  );
}

// ---- Create form -----------------------------------------------------

export function CreateTokenForm(props: {
  slug: string;
  onCreated: (created: CreatedApiTokenResponse) => void;
  onError: (msg: string) => void;
  onCancel: () => void;
}) {
  const [params] = useSearchParams();
  const [label, setLabel] = useState('');
  // 'read' = the plain read token; anything else is a purpose key (V108-1-1).
  const [kind, setKind] = useState<string>(params.get('purpose') ?? 'read');
  const [systemId, setSystemId] = useState(params.get('system') ?? '');
  const [systemError, setSystemError] = useState('');
  const [busy, setBusy] = useState(false);
  const systemSelect = useRef<HTMLSelectElement>(null);
  const purposes = useQuery({ queryKey: queryKeys.apiTokenPurposes(props.slug), queryFn: () => listApiTokenPurposes(props.slug), staleTime: 300_000 });
  const chosen = purposes.data?.purposes.find((p) => p.key === kind) ?? null;
  const systemMode = chosen ? purposeSystemMode(chosen) : 'none';
  const needsSystem = systemMode === 'required';
  // V108-8-7: an optional-system purpose may name a system; '' is "a new system".
  const takesSystem = systemMode !== 'none';
  const systems = useQuery({
    queryKey: queryKeys.systems(props.slug),
    queryFn: () => listWorkspaceSystems(props.slug),
    staleTime: 60_000,
    enabled: takesSystem,
  });
  const systemList = systems.data?.systems ?? [];
  // V108-8-7 critics: the value submitted is always one the select shows. An id from ?system= (or an
  // earlier choice) that isn't in this workspace's loaded list counts as none.
  const shownSystemId = systemList.some((s) => s.system_id === systemId) ? systemId : '';
  const forSystem = (purposes.data?.purposes ?? []).filter((p) => purposeSystemMode(p) !== 'none');
  const forWorkspace = (purposes.data?.purposes ?? []).filter((p) => purposeSystemMode(p) === 'none');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsSystem && !shownSystemId) {
      setSystemError('Choose the system this token is for.');
      systemSelect.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const created = await createApiToken(props.slug, chosen
        ? { label: label.trim(), purpose: chosen.key, ...(takesSystem && shownSystemId ? { system_id: shownSystemId } : {}) }
        : { label: label.trim() });
      props.onCreated(created);
      setLabel('');
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Failed to create token.');
    } finally {
      setBusy(false);
    }
  };

  // Switching between an optional-system purpose ('' = a new system) and a required one ('' = not
  // chosen) starts the system choice over, so a system picked for one never carries into the other.
  const pickKind = (key: string) => {
    const next = purposes.data?.purposes.find((p) => p.key === key);
    if ((next ? purposeSystemMode(next) : 'none') !== systemMode) setSystemId('');
    setKind(key);
    setSystemError('');
  };

  const option = (key: string, text: string, hint?: string) => (
    <label key={key} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
      <input type="radio" name="token-kind" value={key} checked={kind === key} onChange={() => pickKind(key)} aria-describedby={hint ? `token-kind-${key}-hint` : undefined} />
      <span>
        {text}
        {hint && <span id={`token-kind-${key}-hint`} style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>{hint}</span>}
      </span>
    </label>
  );
  const legend = { fontSize: 'var(--font-size-3xs)', fontWeight: 600, marginBottom: 'var(--space-1)' } as const;
  const group = { margin: 0, padding: 0, border: 0, display: 'grid', gap: 'var(--space-2)' } as const;

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-4) var(--space-5)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>Create API token</h2>
      <fieldset style={group}>
        <legend style={legend}>For the workspace</legend>
        {option('read', "Read this workspace's data")}
        {forWorkspace.map((p) => option(p.key, p.label, p.hint))}
      </fieldset>
      {purposes.isLoading && <Spinner label="Loading token types" />}
      {purposes.isError && (
        <Alert variant="warning">
          Some token types didn't load.{' '}
          <Button variant="secondary" compact type="button" onClick={() => purposes.refetch()}>Retry</Button>
        </Alert>
      )}
      {forSystem.length > 0 && (
        <fieldset style={group}>
          <legend style={legend}>For one system</legend>
          {forSystem.map((p) => option(p.key, p.label, p.hint))}
        </fieldset>
      )}
      {systemMode === 'optional' && (
        <>
          <Field
            id="token-system"
            label="System"
            hint={shownSystemId ? 'Also updates this system and adds its use cases.' : 'Registers new systems with gac init.'}
          >
            <Select ref={systemSelect} id="token-system" value={shownSystemId} onChange={(e) => setSystemId(e.target.value)}>
              <option value="">A new system (register from the terminal)</option>
              {systemList.map((s) => <option key={s.system_id} value={s.system_id}>{s.name}</option>)}
            </Select>
          </Field>
          {systems.isLoading && <Spinner label="Loading systems" />}
          {systems.isError && (
            <Alert variant="warning">
              Your existing systems didn't load. A token for a new system still works.{' '}
              <Button variant="secondary" compact type="button" onClick={() => systems.refetch()}>Retry</Button>
            </Alert>
          )}
        </>
      )}
      {needsSystem && (systems.isLoading ? (
        <Spinner label="Loading systems" />
      ) : systems.isError ? (
        <Alert variant="error">
          Systems couldn't load.{' '}
          <Button variant="secondary" compact type="button" onClick={() => systems.refetch()}>Retry</Button>
        </Alert>
      ) : systemList.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)' }}>
          No systems yet. <Link to={`/w/${encodeURIComponent(props.slug)}/app/systems/new`}>Add a system</Link>
        </p>
      ) : (
        <Field id="token-system" label="System" required error={systemError || undefined}>
          <Select ref={systemSelect} id="token-system" value={shownSystemId} onChange={(e) => { setSystemId(e.target.value); setSystemError(''); }}>
            <option value="">Choose a system</option>
            {systemList.map((s) => <option key={s.system_id} value={s.system_id}>{s.name}</option>)}
          </Select>
        </Field>
      ))}
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
        Token is auto-revoked if your workspace membership ends.
      </p>
      <Field id="token-label" label="Label">
        <Input
          id="token-label"
          type="text"
          required
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Datadog Prod · GRC Rollup · Weekly board pull"
          autoComplete="off"
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || (needsSystem && systemList.length === 0)}>
          {busy ? 'Creating…' : 'Create token'}
        </Button>
      </div>
    </form>
  );
}

// ---- Token card -----------------------------------------------------

function TokenCard(props: {
  slug: string;
  token: ApiTokenSummary;
  canManage: boolean;
  onError: (msg: string) => void;
  onChanged: () => void;
}) {
  const { token: t } = props;
  const [showRevoke, setShowRevoke] = useState(false);
  const revoked = t.revoked_at != null;
  return (
    <section
      aria-label={t.label}
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor: revoked ? 'var(--border)' : 'var(--band-green)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
        opacity: revoked ? 0.7 : 1,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'grid', gap: '2px' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
            {t.label}
          </h3>
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
            }}
            title={`${t.prefix}… · SHA-256 stored`}
          >
            {t.prefix}…
          </code>
        </div>
        <StatusPill revoked={revoked} />
      </header>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
          gap: 'var(--space-2) var(--space-4)',
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
        }}
      >
        <MetaRow label="Role at create">{t.role_at_creation}</MetaRow>
        <MetaRow label="Scopes">
          {t.scopes.length > 0 ? t.scopes.join(' · ') : 'read'}
        </MetaRow>
        <MetaRow label="Created">
          <time dateTime={t.created_at}>{fmtDate(t.created_at)}</time>
        </MetaRow>
        <MetaRow label="Last used">
          {t.last_used_at ? (
            <time dateTime={t.last_used_at}>{fmtDate(t.last_used_at)}</time>
          ) : (
            <span style={{ color: 'var(--text-dim)' }}>never</span>
          )}
        </MetaRow>
        {revoked && t.revoked_reason ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <MetaRow label="Revoked">
              <time dateTime={t.revoked_at!}>{fmtDate(t.revoked_at!)}</time>
              <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                “{t.revoked_reason}”
              </span>
            </MetaRow>
          </div>
        ) : null}
      </dl>
      {props.canManage && !revoked ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 'var(--space-1)' }}>
          <Button variant="ghost" compact onClick={() => setShowRevoke((s) => !s)}>
            {showRevoke ? 'Cancel revoke' : 'Revoke token'}
          </Button>
        </div>
      ) : null}
      {showRevoke ? (
        <RevokeForm
          slug={props.slug}
          tokenId={t.token_id}
          onDone={() => {
            setShowRevoke(false);
            props.onChanged();
          }}
          onCancel={() => setShowRevoke(false)}
          onError={props.onError}
        />
      ) : null}
    </section>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
        {children}
      </dd>
    </div>
  );
}

function StatusPill({ revoked }: { revoked: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: revoked ? 'var(--surface-alt)' : 'var(--success-bg)',
        color: revoked ? 'var(--text-muted)' : 'var(--success-fg)',
      }}
    >
      {revoked ? 'Revoked' : 'Active'}
    </span>
  );
}

function RevokeForm(props: {
  slug: string;
  tokenId: string;
  onDone: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await revokeApiToken(props.slug, props.tokenId, reason.trim());
      props.onDone();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--danger-bg)',
        border: '1px solid var(--band-red)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <strong style={{ color: 'var(--danger-fg)', fontWeight: 700 }}>Revoke API token</strong>
      <p style={{ margin: 0, color: 'var(--danger-fg)', fontSize: 'var(--font-size-3xs)' }}>
        Any consumer using this token will start receiving 401 responses immediately. Reason is
        chained into the audit log.
      </p>
      <Field id="revoke-token-reason" label="Reason (audit-logged)">
        <Textarea
          id="revoke-token-reason"
          required
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || reason.trim().length < 3}>
          {busy ? 'Revoking…' : 'Confirm revoke'}
        </Button>
      </div>
    </form>
  );
}
